import React, { useMemo, useRef } from "react";
import { Alert, Button } from "reactstrap";
import { Link } from "react-router-dom";
import * as ModalComp from "@repoflow/form-schema/modal";
import { List } from "@repoflow/components/list-cursor";
import { useSectionContext } from "@repoflow/components/section";
import * as BlogPostQL from "queries/blog-post";
import * as widgets from "widgets/register";
import BlogPostSchema from "@common/schema/blog-post/public";
import { isPublished } from "@common/schema/blog-post/status";

// Pick a PUBLISHED post — for a collection's membership, where a draft would
// be a member nobody can see. The same shape as components/archive-selector,
// and for the same reason a third specific picker rather than one generic one:
// what makes a row pickable (`isPublished`) and what a row shows are this
// picker's, not a parameter's.
//
// Lists the DRAFTS with their status rather than the published table, so an
// author sees every post they have and why some cannot be picked, instead of
// a list that is silently shorter than the one on the Posts tab.
const FIELDS = {
  "/Public/BlogPost": {
    fields: ["title", "slug", "status", "updatedAt"],
  },
};

const Row = ({ item, onSelect, close, chosen }) => {
  if (!isPublished(item)) {
    return <span className="text-muted">Not published</span>;
  }

  if (chosen.includes(item.postId)) {
    return <span className="text-muted">Already in the collection</span>;
  }

  return (
    <Button
      type="button"
      color="primary"
      onClick={() => {
        onSelect(item);
        close();
      }}
    >
      Select
    </Button>
  );
};

const Body = ({ close, context: { onSelect, siteId, chosen } }) => {
  const handlers = useRef({ onSelect, close });
  handlers.current = { onSelect, close };

  const Content = useMemo(
    () =>
      ({ item }) => (
        <Row
          item={item}
          chosen={chosen}
          onSelect={(...args) => handlers.current.onSelect(...args)}
          close={() => handlers.current.close()}
        />
      ),
    [chosen]
  );

  return (
    <List
      path={"viewer.account.sites.get.blog.posts"}
      query={BlogPostQL.list}
      variables={{ siteId }}
      schema={BlogPostSchema}
      // The registry, because a widget name is a demand: the post schema names
      // `image` on a field this listing never shows, and getField() resolves
      // one for every property regardless (the P132 story). The archive
      // picker this copies could omit it only because its schema names none.
      widgets={widgets}
      target={{ $ref: "/Public/BlogPost" }}
      config={FIELDS}
      fetchPolicy="cache-and-network"
      empty={
        <>
          No posts on this site yet.{" "}
          <Link to={`/site/${siteId}/blog/post/new`}>Write one</Link>.
        </>
      }
      content={Content}
    />
  );
};

export const Component = ({ state, onSelect, chosen = [] }) => {
  const context = useSectionContext();
  const siteId = context && context.attributes ? context.attributes.siteId : null;

  if (!siteId) {
    return (
      <ModalComp.Component state={state} title="Select a post">
        {() => (
          <Alert color="danger">
            This picker is not inside a site, so it has nothing to list.
          </Alert>
        )}
      </ModalComp.Component>
    );
  }

  return (
    <ModalComp.Component
      state={state}
      title={"Select a post"}
      context={{ onSelect, siteId, chosen }}
    >
      {Body}
    </ModalComp.Component>
  );
};

export const SelectButton = ({ onSelect, chosen, children = "Add a post", ...rest }) => {
  const state = ModalComp.useModalState(false);

  return (
    <>
      <Button
        type="button"
        color="primary"
        onClick={(e) => {
          e.preventDefault();
          state.open();
        }}
        {...rest}
      >
        {children}
      </Button>
      <Component state={state} onSelect={onSelect} chosen={chosen} />
    </>
  );
};
